import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "./bbs_bracu_logo_white.png";
import img from "./dash_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";

const Dropdown = ({ title, links, openDropdown, setOpenDropdown }) => {
  const dropdownRef = useRef(null);
  const isOpen = openDropdown === title;

  const toggleDropdown = () => {
    setOpenDropdown(isOpen ? null : title);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpenDropdown]);

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button className="dropbtn" onClick={toggleDropdown}>
        {title}
      </button>
      <div className={`dropdown-content ${isOpen ? "show" : ""}`}>
        {links.map((link) => (
          <a key={link.href} href={link.href}>
            {link.label}
          </a>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [facultyInfo, setFacultyInfo] = useState({ salutation: "", name: "" });

  const [searchQuery, setSearchQuery] = useState("");
  const token = localStorage.getItem("token");
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token");

        const res = await axios.get(`${BACKEND_URL}/api/personal_information`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const info = res.data?.[0] || {};
        setFacultyInfo({
          salutation: info.salutation || "",
          name: info.name || "Faculty Member",
        });
      } catch {
        toast.error("Session expired or access denied. Please login.");
        navigate("/login");
      }
    };

    fetchFaculty();
  }, [navigate]);

  const handleDownloadCV = () => {
    if (!token) {
      toast.warning("You need to login first!");
      return navigate("/login");
    }

    const toastId = toast.info(
      <div>
        Generate and download your CV as a PDF?{" "}
        <button
          onClick={async () => {
            try {
              const response = await axios.get(
                `${BACKEND_URL}/api/download-cv`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: "blob",
                }
              );

              const disposition = response.headers["content-disposition"];
              const match =
                disposition && disposition.match(/filename="?(.+)"?/);
              const filename = match
                ? decodeURIComponent(match[1])
                : "faculty_cv.pdf";

              const url = window.URL.createObjectURL(new Blob([response.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);

              toast.update(toastId, {
                render: "Downloading CV...",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch {
              toast.update(toastId, {
                render: "Failed to download CV. Please try again.",
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    toast.info("Logging out...");
    navigate("/");
  };

  // Updated dropdown sections with new structure
  const dropdownSections = [
    {
      title: "Profile",
      links: [
        { label: "Personal Information", href: "/personal_info" },
        { label: "Research Profile", href: "/research-profile" },
        { label: "Education", href: "/education" },
        { label: "Awards", href: "/awards" },
      ],
    },
    {
      title: "Academic Activities",
      links: [
        { label: "Academic Experience", href: "/academic-experience" },
        {
          label: "Academic Administrative Role",
          href: "/academic-administrative-role",
        },
        { label: "Academic Events Organized", href: "/event-organized" },
        { label: "New Program Developments", href: "/new-program" },

        { label: "New Degree Course Developments", href: "/new-degree-dev" },
{
      label: "Course Coordination",
      href: "/course-coordination",
    },
        {
          label: "Professional Certificate Course Developments",
          href: "/pro-certificate-dev",
        },

        {
          label: "Keynote Speeches/Conference Session Chairs",
          href: "/conference-session-chair",
        },
        {
          label: "Seminars/Workshops/Speeches Delivered",
          href: "/speech-delivered",
        },

        { label: "Committee Services", href: "/committee-service" },
        { label: "Contribution to Academic Policy", href: "/academic-policy" },
        {
          label: "International Academic Affiliation/Contribution",
          href: "/international-academic",
        },
      ],
    },
    {
      title: "Scholarly/Intellectual Activities",
      links: [
        { label: "Academic Journal Articles", href: "/journal-publications" },
        { label: "Edited Books", href: "/book" },
        { label: "Book Chapters", href: "/book-chapters" },

        {
          label: "Conference Proceedings/Presentations",
          href: "/conference-proceeding",
        },
        { label: "Case Publications", href: "/case-publication" },
        { label: "Op-Eds", href: "/op-ed" },

        { label: "Trade Journal Articles", href: "/trade-journal" },
        { label: "White Papers/Policy Briefs", href: "/policy-brief" },

        { label: "Industry Reports", href: "/industry-report" },

        { label: "Research Grants", href: "/research-grant" },
        { label: "Textbooks Published", href: "/textbook" },
        {
          label: "Portfolio of Scholarly / Intellectual Contributions",
          href: "/intellectual-contributions",
        },
      ],
    },
    {
      title: "Professional Activities",
      links: [
        {
          label: "Professional Certifications",
          href: "/professional-certification",
        },
        { label: "Industry Experience", href: "/industry-experience" },

        { label: "Business Ownerships", href: "/business-owner" },
        { label: "Board Memberships", href: "/brand-member" },
        { label: "Consultation Services", href: "/consultation" },
        { label: "Trainings Delivered", href: "/training-delivered" },

        {
          label: "Organizational Memberships",
          href: "/organizational-membership",
        },

        {
          label: "Policy Advisory Role",
          href: "/policy-advisory",
        },
        {
          label: "Public-Interest Institutions Engagement",
          href: "/public-interest",
        },

        {
          label: "Intellectual Property Commercialization",
          href: "/intellectual-property",
        },
      ],
    },

    {
      title: "Editorial and Review Activities",
      links: [
        { label: "Editorial Services", href: "/editorial-service" },
        {
      label: "Book Review International",
      href: "/book-review-intl",
    },

    {
      label: "Book Review Local",
      href: "/book-review-local",
    },
        {
          label: "Graduate Research Supervision",
          href: "/research-supervision",
        },
        {
          label: "Research Grant Proposals Reviewed",
          href: "/grant-proposal-reviewed",
        },
      ],
    },

    {
      title: "Community and Outreach Activities",
      links: [
        {
          label: "Community/Social Service Activities",
          href: "/social-service",
        },
        {
          label: "Events/Workshops/Field Trip Organized ",
          href: "/event-workshop",
        },
        {
          label: "Community Outreach/ Engagement Service Activities",
          href: "/community-outreach",
        },
        { label: "Contribution to Media", href: "/contribution-media" },
      ],
    },
  ];

  // Filter sections for search
  const filteredSections = dropdownSections
    .map((section) => ({
      ...section,
      links: section.links.filter((link) =>
        link.label.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((section) => section.links.length > 0);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        fontFamily: "Arial",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <style>{`
        .title-bar { display: flex; align-items: center; justify-content: center; background-color: #152e74; padding: 15px 20px; position: relative; }
        .title-bar h1 { margin: 0; font-size: 1.8rem; color: white; text-align: center; flex: 1; }
        .title-bar img { position: absolute; left: 20px; height: 100px; width: auto; top: 50%; transform: translateY(-50%); }
        .logout-btn { position: absolute; top: 50%; right: 20px; transform: translateY(-50%); background: #e74c3c; color: white; border: none; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-weight: bold; transition: background-color 0.3s ease; }
        .logout-btn:hover { background-color: #c0392b; }
        .navbar { display: flex; justify-content: center; align-items: center; background-color: #2a4388ff; color: white; padding: 10px 20px; flex-wrap: wrap; }
        .nav-left { display: flex; gap: 15px; flex-wrap: wrap; align-items: center; }
        .dropbtn { background-color: #2a4388ff; color: white; padding: 8px 15px; border: none; cursor: pointer; font-size: 14px; border-radius: 5px; transition: 0.3s; }
        .dropbtn:hover { background-color: #3b82f6; }
        .dropdown { position: relative; display: inline-block; }
        .dropdown-content { position: absolute; background-color: white; min-width: 200px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); border-radius: 5px; top: 110%; z-index: 10; opacity: 0; visibility: hidden; transform: translateY(-10px); transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s; }
        .dropdown-content.show { opacity: 1; visibility: visible; transform: translateY(0); }
        .dropdown-content a { color: #1e3a8a; padding: 10px 15px; text-decoration: none; display: block; font-size: 14px; }
        .dropdown-content a:hover { background-color: #f0f0f0; }
        .container { flex: 1; text-align: center; padding: 40px 60px; margin: 20px auto; max-width: 900px; background-color: rgba(255,255,255,0.3); backdrop-filter: blur(12px); border-radius: 15px; box-shadow: 0 8px 20px rgba(0,0,0,0.15); display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 200px; }
        .container h2 { color: #1e3a8a; font-size: 2rem; margin-bottom: 15px; }
        .btn { background-color: #3b82f6; color: white; padding: 10px 20px; border-radius: 6px; border: none; cursor: pointer; font-weight: bold; transition: 0.3s; }
        .btn:hover { background-color: #2563eb; }
        footer { text-align: center; padding: 5px 0; font-size: 12px; color: black; margin-top: auto; background-color: transparent; border-top: 1px solid rgba(255,255,255,0.2); }
        .search-bar { display: flex; justify-content: center; margin: 15px 0; }
        .search-bar input { width: 50%; padding: 8px 12px; border-radius: 6px; border: 1px solid #ccc; font-size: 14px; outline: none; transition: 0.3s; }
        .search-bar input:focus { border-color: #3b82f6; box-shadow: 0 0 5px rgba(59,130,246,0.5); }
        .search-results { display: flex; flex-direction: column; align-items: center; gap: 10px; margin-bottom: 20px; }
        .search-section { background-color: rgba(255,255,255,0.7); padding: 15px 20px; border-radius: 10px; width: 80%; max-width: 700px; text-align: left; }
        .search-section h4 { margin: 0 0 8px 0; color: #1e3a8a; }
        .search-section a { display: block; text-decoration: none; color: #0b2254; padding: 2px 0; }
        .search-section a:hover { text-decoration: underline; }
      `}</style>

      <div className="title-bar">
        <img src={logo} alt="BBS Logo" />
        <h1>Faculty Dashboard</h1>
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>

      <div className="navbar">
        <div className="nav-left">
          {dropdownSections.map((section) => (
            <Dropdown
              key={section.title}
              title={section.title}
              links={section.links}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          ))}
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search sections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {searchQuery && (
        <div className="search-results">
          {filteredSections.map((section) => (
            <div key={section.title} className="search-section">
              <h4>{section.title}</h4>
              {section.links.map((link) => (
                <a key={link.href} href={link.href}>
                  {link.label}
                </a>
              ))}
            </div>
          ))}
        </div>
      )}

      {!searchQuery && (
        <div className="container">
          <h2>
            Welcome! {facultyInfo.salutation} {facultyInfo.name}
          </h2>

          <p>Manage all your information from here.</p>
          <button className="btn" onClick={handleDownloadCV}>
            Download CV
          </button>
        </div>
      )}

      <footer>
        <p>Copyright © 2025 - All rights reserved by BRAC Business School</p>
        <p>
          Developed By Refah Tasnia, Research Associate, BRAC Business School and Razia Zaman Ela, Administrative Officer, BRAC Business School
        </p>
      </footer>
    </div>
  );
};

export default Dashboard;
